import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Breadcrumbs, Button, Chip, Grid, IconButton, Link, Paper, Stack, Tab, Table, TableBody,
  TableCell, TableHead, TableRow, Tabs, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import type { ActionItem, Contact } from '../api/types';
import {
  useChangeCustomerOwner, useCustomer, useCustomerActions, useCustomerContacts,
  useCustomerOpportunities, useDeleteContact, useDeleteCustomer, usePartners,
} from '../api/hooks';
import { ConfirmDialog, EmptyState, ErrorState, LoadingState, StageChip, UserText } from '../components/common/Common';
import { CustomerFormDialog } from '../components/forms/CustomerFormDialog';
import { ContactFormDialog } from '../components/forms/ContactFormDialog';
import { OpportunityFormDialog } from '../components/forms/OpportunityFormDialog';
import { ActionFormDialog } from '../components/forms/ActionFormDialog';
import { OwnerPickerDialog } from '../components/forms/OwnerPickerDialog';
import { ActionTimeline } from '../components/timeline/ActionTimeline';
import { formatCurrency, formatDate } from '../components/common/format';

type TabKey = 'opportunities' | 'contacts' | 'activity';

export function CustomerDetailPage() {
  const { id } = useParams();
  const customerId = Number(id);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: customer, isLoading, error, refetch } = useCustomer(customerId);
  const { data: contacts } = useCustomerContacts(customerId);
  const { data: opportunities } = useCustomerOpportunities(customerId);
  const { data: actions } = useCustomerActions(customerId);
  const { data: partners } = usePartners();
  const deleteCustomer = useDeleteCustomer();
  const deleteContact = useDeleteContact();
  const changeOwner = useChangeCustomerOwner();

  const [tab, setTab] = useState<TabKey>('opportunities');
  const [editOpen, setEditOpen] = useState(false);
  const [contactDialog, setContactDialog] = useState<{ open: boolean; contact: Contact | null }>({ open: false, contact: null });
  const [oppOpen, setOppOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState<{ open: boolean; action: ActionItem | null }>({ open: false, action: null });
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [deletingCustomer, setDeletingCustomer] = useState(false);
  const [deletingContact, setDeletingContact] = useState<Contact | null>(null);

  if (isLoading) return <LoadingState label={t('customerDetail.loading')} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!customer) return null;

  const partnerType = partners?.items.find((p) => p.id === customer.partnerId)?.defaultRelationshipType;

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/customers" underline="hover" color="text.secondary">
          {t('nav.customers')}
        </Link>
        <Typography color="text.primary"><UserText>{customer.name}</UserText></Typography>
      </Breadcrumbs>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h1"><UserText>{customer.name}</UserText></Typography>
          <Typography variant="body2" color="text.secondary">
            {[customer.country, customer.emailDomain].filter(Boolean).join(' · ')
              || t('customerDetail.noCountryOrDomain')}
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
            {customer.partnerName && (
              <Stack direction="row" spacing={0.5} alignItems="center" data-testid="customer-partner-header">
                <HandshakeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2"><UserText>{customer.partnerName}</UserText></Typography>
                {partnerType && (
                  <Chip size="small" variant="outlined" label={t(`partner.relationship.${partnerType}`)} />
                )}
              </Stack>
            )}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PersonOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" data-testid="customer-owner">
                {customer.ownerName
                  ? <UserText>{customer.ownerName}</UserText>
                  : <Box component="span" sx={{ color: 'text.disabled' }}>{t('owner.unassigned')}</Box>}
              </Typography>
              <Tooltip title={t('owner.changeOwner')}>
                <IconButton size="small" onClick={() => setOwnerOpen(true)} data-testid="change-customer-owner">
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)} data-testid="edit-customer">
            {t('common.edit')}
          </Button>
          <Button
            startIcon={<DeleteOutlineIcon />}
            color="error"
            onClick={() => setDeletingCustomer(true)}
            data-testid="delete-customer"
          >
            {t('common.delete')}
          </Button>
        </Stack>
      </Stack>

      {(customer.website || customer.notes) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: '10px' }}>
          <Grid container spacing={2}>
            {customer.website && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="overline" color="text.secondary">{t('common.website')}</Typography>
                <Typography variant="body2">
                  <Link href={customer.website} target="_blank" rel="noreferrer">{customer.website}</Link>
                </Typography>
              </Grid>
            )}
            {customer.notes && (
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="overline" color="text.secondary">{t('common.notes')}</Typography>
                <Typography variant="body2"><UserText multiline>{customer.notes}</UserText></Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      <Tabs value={tab} onChange={(_, v: TabKey) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab
          value="opportunities"
          label={t('customerDetail.tabs.opportunities', { count: opportunities?.length ?? 0 })}
          data-testid="tab-opportunities"
        />
        <Tab
          value="contacts"
          label={t('customerDetail.tabs.contacts', { count: contacts?.length ?? 0 })}
          data-testid="tab-contacts"
        />
        <Tab
          value="activity"
          label={t('customerDetail.tabs.activity', { count: actions?.items.length ?? 0 })}
          data-testid="tab-activity"
        />
      </Tabs>

      {tab === 'opportunities' && (
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ mb: 2 }}
            onClick={() => setOppOpen(true)}
            data-testid="new-opportunity"
          >
            {t('customerDetail.newOpportunity')}
          </Button>
          {(opportunities?.length ?? 0) === 0 ? (
            <EmptyState
              title={t('customerDetail.noOpportunities')}
              description={t('customerDetail.noOpportunitiesDescription')}
            />
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: '10px', overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('customerDetail.opportunityColumns.title')}</TableCell>
                    <TableCell>{t('customerDetail.opportunityColumns.stage')}</TableCell>
                    <TableCell align="right">{t('customerDetail.opportunityColumns.value')}</TableCell>
                    <TableCell>{t('customerDetail.opportunityColumns.expectedClose')}</TableCell>
                    <TableCell>{t('customerDetail.opportunityColumns.nextStep')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {opportunities?.map((o) => (
                    <TableRow
                      key={o.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/opportunities/${o.id}`)}
                      data-testid={`opp-row-${o.id}`}
                    >
                      <TableCell><UserText>{o.title}</UserText></TableCell>
                      <TableCell><StageChip stage={o.stage} outcome={o.closedOutcome} /></TableCell>
                      <TableCell align="right">{formatCurrency(o.estimatedValue, o.currency)}</TableCell>
                      <TableCell>{formatDate(o.expectedCloseDate)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color={o.nextStepDueDate ? 'text.primary' : 'text.disabled'}>
                          <UserText>{o.nextStep ?? '—'}</UserText>
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}

      {tab === 'contacts' && (
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ mb: 2 }}
            onClick={() => setContactDialog({ open: true, contact: null })}
            data-testid="new-contact"
          >
            {t('customerDetail.newContact')}
          </Button>
          {(contacts?.length ?? 0) === 0 ? (
            <EmptyState title={t('customerDetail.noContacts')} description={t('customerDetail.noContactsDescription')} />
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: '10px', overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('customerDetail.contactColumns.name')}</TableCell>
                    <TableCell>{t('customerDetail.contactColumns.jobTitle')}</TableCell>
                    <TableCell>{t('customerDetail.contactColumns.email')}</TableCell>
                    <TableCell>{t('customerDetail.contactColumns.phone')}</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts?.map((c) => (
                    <TableRow key={c.id} hover data-testid={`contact-row-${c.id}`}>
                      <TableCell><UserText>{c.name}</UserText></TableCell>
                      <TableCell><UserText>{c.jobTitle ?? '—'}</UserText></TableCell>
                      <TableCell>{c.email ?? '—'}</TableCell>
                      <TableCell>{c.phone ?? '—'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => setContactDialog({ open: true, contact: c })}>
                          {t('common.edit')}
                        </Button>
                        <Button size="small" color="error" onClick={() => setDeletingContact(c)}>
                          {t('common.delete')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}

      {tab === 'activity' && (
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ mb: 2 }}
            onClick={() => setActionDialog({ open: true, action: null })}
            data-testid="new-action"
          >
            {t('customerDetail.logAction')}
          </Button>
          <ActionTimeline
            actions={actions?.items ?? []}
            showOpportunity
            onEdit={(a) => setActionDialog({ open: true, action: a })}
          />
        </Box>
      )}

      <CustomerFormDialog open={editOpen} customer={customer} onClose={() => setEditOpen(false)} />
      <ContactFormDialog
        open={contactDialog.open}
        customerId={customerId}
        contact={contactDialog.contact}
        onClose={() => setContactDialog({ open: false, contact: null })}
      />
      <OpportunityFormDialog
        open={oppOpen}
        customerId={customerId}
        contacts={contacts ?? []}
        onClose={() => setOppOpen(false)}
        onSaved={(o) => navigate(`/opportunities/${o.id}`)}
      />
      <ActionFormDialog
        open={actionDialog.open}
        customerId={customerId}
        opportunityId={null}
        action={actionDialog.action}
        onClose={() => setActionDialog({ open: false, action: null })}
      />
      <OwnerPickerDialog
        open={ownerOpen}
        currentOwnerUserId={customer.ownerUserId}
        busy={changeOwner.isPending}
        error={changeOwner.error}
        onClose={() => setOwnerOpen(false)}
        onSave={async (ownerUserId) => {
          await changeOwner.mutateAsync({ id: customerId, ownerUserId });
          setOwnerOpen(false);
        }}
      />

      <ConfirmDialog
        open={deletingCustomer}
        title={t('customerDetail.deleteTitle')}
        message={t('customerDetail.deleteMessage')}
        busy={deleteCustomer.isPending}
        onClose={() => setDeletingCustomer(false)}
        onConfirm={async () => {
          await deleteCustomer.mutateAsync(customerId);
          setDeletingCustomer(false);
          navigate('/customers');
        }}
      />
      <ConfirmDialog
        open={Boolean(deletingContact)}
        title={t('customerDetail.deleteContactTitle')}
        message={t('customerDetail.deleteContactMessage')}
        busy={deleteContact.isPending}
        onClose={() => setDeletingContact(null)}
        onConfirm={() => {
          if (deletingContact) deleteContact.mutate(deletingContact.id);
          setDeletingContact(null);
        }}
      />
    </Box>
  );
}
